import { useEffect, useState } from 'react';
import { useAppContext } from '../store/AppContext';
import bookingValidationService from '../services/bookingValidationService';

/**
 * Hook centralisé pour valider le panier
 * Retourne les erreurs, changements de prix et produits non disponibles
 */
const useBookingCartValidation = () => {
  const [{ players, hosting }] = useAppContext();
  const [validation, setValidation] = useState({
    isValid: true,
    errors: [],
    priceChanges: [],
    unavailableProducts: [],
    isLoading: true,
  });

  // Gestion de l'affichage du warning de changement de prix
  // Liste des productId dont l'avertissement a été dismiss (par suppression ou validation d'étape)
  const [dismissedPriceChangeProducts, setDismissedPriceChangeProducts] = useState([]);

  // Reset si le panier est vidé
  useEffect(() => {
    if (!players || players.length === 0) {
      setDismissedPriceChangeProducts([]);
    }
  }, [players]);

  // Appelée quand on supprime un produit dont le prix a changé
  const dismissPriceChange = (productId) => {
    setDismissedPriceChangeProducts((prev) => [...new Set([...prev, productId])]);
  };

  // Appelée quand on valide l'étape (suivant)
  // Si un nouveau changement de prix arrive après, il sera affiché (car non présent dans dismissedPriceChangeProducts)
  const dismissAllPriceChanges = () => {
    setDismissedPriceChangeProducts((prev) => {
      const currentIds = validation.priceChanges.map((change) => change.productId);
      // On garde les anciens dismiss, mais on ajoute ceux de l'étape validée
      return [...new Set([...prev, ...currentIds])];
    });
  };

  /**
   * Détecte localement les changements de prix en comparant les champs
   * `oldPrice` et `price` présents dans l'AppContext (`players` et `hosting`).
   * IMPORTANT: ne réalise aucun appel réseau et ne convertit aucune unité —
   * les valeurs retournées sont strictement celles stockées dans l'AppContext
   * (offres, options et hébergement sont stockés en euros dans l'AppContext).
   */
  const checkServerPriceChanges = async () => {
    try {
      const priceChanges = [];

      // Parcourir les joueurs et détecter les changements sur les offres, options et extras
      if (players && players.length > 0) {
        players.forEach((player) => {
          // Offres (stage) — productId lié au stage
          const stage = player.stage;
          if (stage && Array.isArray(stage.offers)) {
            stage.offers.forEach((offer) => {
              if (typeof offer.oldPrice === 'number' && offer.oldPrice !== offer.price) {
                const productId = (typeof stage.productId !== 'undefined' && stage.productId !== null)
                  ? stage.productId
                  : (offer.productId || null);
                const showOldPrice = (typeof offer.oldPrice === 'number' && typeof offer.price === 'number')
                  ? (offer.price < offer.oldPrice)
                  : false;

                priceChanges.push({
                  type: 'detailed',
                  productId,
                  productName: stage.productName || offer.label || '',
                  oldPrice: offer.oldPrice,
                  newPrice: offer.price,
                  showOldPrice,
                  message: '',
                  severity: 'warning',
                });
              }
            });
          }

          // Options (quantities) — prix en euros
          if (Array.isArray(player.options)) {
            player.options.forEach((option) => {
              if (Array.isArray(option.quantities)) {
                option.quantities.forEach((quantity) => {
                  if (typeof quantity.oldPrice === 'number' && quantity.oldPrice !== quantity.price) {
                    const showOldPrice = (typeof quantity.oldPrice === 'number' && typeof quantity.price === 'number')
                      ? (quantity.price < quantity.oldPrice)
                      : false;
                    priceChanges.push({
                      type: 'detailed',
                      productId: option.productId || null,
                      productName: option.label || '',
                      oldPrice: quantity.oldPrice,
                      newPrice: quantity.price,
                      showOldPrice,
                      message: '',
                      severity: 'warning',
                    });
                  }
                });
              }
            });
          }

          // Autres options / extras
          if (Array.isArray(player.otherOptions)) {
            player.otherOptions.forEach((option) => {
              if (Array.isArray(option.quantities)) {
                option.quantities.forEach((quantity) => {
                  if (typeof quantity.oldPrice === 'number' && quantity.oldPrice !== quantity.price) {
                    const showOldPrice = (typeof quantity.oldPrice === 'number' && typeof quantity.price === 'number')
                      ? (quantity.price < quantity.oldPrice)
                      : false;
                    priceChanges.push({
                      type: 'detailed',
                      productId: option.productId || null,
                      productName: option.label || '',
                      oldPrice: quantity.oldPrice,
                      newPrice: quantity.price,
                      showOldPrice,
                      message: '',
                      severity: 'warning',
                    });
                  }
                });
              }
            });
          }
        });
      }

      // Hébergement — structure globale `hosting` (prix en euros)
      if (hosting && hosting.length > 0) {
        hosting.forEach((host) => {
          if (typeof host.oldPrice === 'number' && host.oldPrice !== host.price) {
            const productId = host.productId || host.id || null;
            const showOldPrice = (typeof host.oldPrice === 'number' && typeof host.price === 'number')
              ? (host.price < host.oldPrice)
              : false;
            priceChanges.push({
              type: 'detailed',
              productId,
              productName: host.label || host.name || '',
              oldPrice: host.oldPrice,
              newPrice: host.price,
              showOldPrice,
              message: '',
              severity: 'warning',
            });
          }
        });
      }

      return priceChanges;
    } catch (error) {
      console.error('Erreur lors de la vérification locale des prix:', error);
      return [];
    }
  };

  /**
   * Lance la validation complète
   */
  const validateCart = async () => {
    try {
      setValidation((prev) => ({ ...prev, isLoading: true }));

      // Valider le stock et les prix locaux
      const clientValidation = await bookingValidationService.validateCompleteCart(
        players,
        hosting,
      );

      // Vérifier aussi les changements de prix locaux (AppContext)
      const localPriceChanges = await checkServerPriceChanges();

      // Normaliser les changements venant du serveur pour inclure `showOldPrice`
      const serverPriceChanges = (clientValidation.priceChanges || []).map((change) => {
        const showOldPrice = (typeof change.showOldPrice !== 'undefined')
          ? change.showOldPrice
          : ((typeof change.oldPrice === 'number' && typeof change.newPrice === 'number')
            ? (change.newPrice < change.oldPrice)
            : false);

        return {
          ...change,
          showOldPrice,
        };
      });

      // Fusionner les changements de prix client (serveur) et locaux (chaque source
      // retourne `oldPrice`/`newPrice` dans leurs unités locales ; ne pas re-normaliser)
      const allPriceChanges = [...serverPriceChanges, ...(localPriceChanges || [])];

      setValidation({
        ...clientValidation,
        priceChanges: allPriceChanges,
        isLoading: false,
      });

      return {
        ...clientValidation,
        priceChanges: allPriceChanges,
      };
    } catch (error) {
      console.error('Erreur validation cart:', error);
      setValidation({
        isValid: true,
        errors: [],
        priceChanges: [],
        unavailableProducts: [],
        isLoading: false,
      });
      return undefined;
    }
  };

  /**
   * Relance la validation à chaque changement du panier
   */
  useEffect(() => {
    validateCart();
  }, [players, hosting]);

  /**
   * Vérifie si un produit est non disponible
   */
  const isProductUnavailable = (productId) => validation.unavailableProducts.includes(productId);

  /**
   * Récupère les changements de prix pour un produit
   */
  const getPriceChanges = (productId) => validation.priceChanges.filter(
    (change) => change.productId === productId,
  );

  /**
   * Récupère les erreurs pour un produit
   */
  const getProductErrors = (productId) => validation.errors.filter(
    (error) => error.productId === productId,
  );

  /**
   * Vérifie si on peut passer à l'étape suivante
   */
  const canProceedToNextStep = () => (
    // On peut procéder si:
    // 1. Il n'y a pas d'erreurs (stock épuisé)
    // 2. OU l'utilisateur a supprimé tous les produits problématiques
    validation.errors.length === 0
  );

  // Ne garder que les priceChanges qui n'ont pas été dismiss et qui sont encore dans le panier
  const activePriceChanges = validation.priceChanges.filter(
    (change) => !dismissedPriceChangeProducts.includes(change.productId)
      && (
        // Correction : comparer player.stage.productId (UUID) avec change.productId (UUID)
        players?.some((player) => (
          (player.stage?.productId === change.productId)
          || player.options?.some((opt) => opt.productId === change.productId)
          || player.otherOptions?.some((opt) => opt.productId === change.productId)
        ))
        || hosting?.some((acc) => acc.productId === change.productId || acc.id === change.productId)
      ),
  );

  return {
    validation,
    validateCart,
    isProductUnavailable,
    getPriceChanges,
    getProductErrors,
    canProceedToNextStep,
    hasPriceChangeWarning: activePriceChanges.length > 0,
    activePriceChanges,
    dismissPriceChange,
    dismissAllPriceChanges,
  };
};

export default useBookingCartValidation;
